<template>
  <div v-loading="loading" class="base-info__container base-flex">
    <div class="base-info__left">
      <h3>{{ $t('computingCluster.detailsInfo.basicConfig') }}</h3>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.teamName') }}</span>
        <span>{{ teamName }}</span>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.teamId') }}</span>
        <span>{{ projectId }}</span>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.downloadApproval') }}</span>
        <span>
          <el-radio-group v-model="approvalFile" :disabled="editDisabled">
            <el-radio :label="'1'" border>{{ $t('computingCluster.detailsInfo.enable') }}</el-radio>
            <el-radio :label="'0'" border>{{ $t('computingCluster.detailsInfo.disable') }}</el-radio>
          </el-radio-group>
        </span>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.watermark') }}</span>
        <span>
          <el-radio-group v-model="waterMarkRadio" :disabled="editDisabled">
            <el-radio :label="'1'" border>{{ $t('computingCluster.detailsInfo.enable') }}</el-radio>
            <el-radio :label="'0'" border>{{ $t('computingCluster.detailsInfo.disable') }}</el-radio>
          </el-radio-group>
        </span>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.nodeLogin') }}</span>
        <span>
          <el-radio-group v-model="loginMaster" :disabled="editDisabled">
            <el-radio :label="'1'" border>{{ $t('computingCluster.detailsInfo.memberCanLogin') }}</el-radio>
            <el-radio :label="'0'" border>{{ $t('computingCluster.detailsInfo.memberCannotLogin') }}</el-radio>
          </el-radio-group>
        </span>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.accountModel') }}</span>
        <span>
          <el-radio-group v-model="accountModel" :disabled="editDisabled || clusterCount">
            <el-radio border label="openldap">openldap</el-radio>
            <el-radio border label="nis">nis</el-radio>
          </el-radio-group>
        </span>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.deployMode') }}</span>
        <span>
          <el-radio-group v-model="deployMode" :disabled="editDisabled || clusterCount">
            <el-radio border label="cluster">{{ $t('computingCluster.detailsInfo.cluster') }}</el-radio>
            <el-radio border label="simple">{{ $t('computingCluster.detailsInfo.simple') }}</el-radio>
          </el-radio-group>
        </span>
      </div>
      <div class="base-margin__bottom-20">
        <div class="base-flex__cross-center">
          <span>{{ $t('computingCluster.detailsInfo.networkBilling') }}</span>
          <span class="base-flex__cross-center">
            <el-select
              v-model="networkChargeType"
              :disabled="editDisabled || clusterCount"
              class="base-margin__right-20 base-width__320"
            >
              <el-option
                v-for="item in networkChargeTypeList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
                <span style="float: left">{{ item.label }}</span>
                <span style="float: right">
                  <el-link :href="item.url" class="base-font-size__12px" target="_blank" type="primary">
                    {{ $t('computingCluster.detailsInfo.details') }}
                  </el-link>
                </span>
              </el-option>
            </el-select>
            <el-link
              :underline="false"
              class="base-font-size__12px"
              href="https://cloud.tencent.com/document/product/1199/51739"
              target="_blank"
              type="primary"
            >
              {{ $t('computingCluster.detailsInfo.networkBillingDesc') }}
            </el-link>
          </span>
        </div>
      </div>
      <div class="base-margin__bottom-20">
        <div class="base-flex__cross-center">
          <span>{{ $t('computingCluster.detailsInfo.userDir') }}</span>
          <span>
            <el-input
              v-model="homePath"
              :disabled="editDisabled || clusterCount"
              :placeholder="$t('computingCluster.detailsInfo.inputHomePath')"
              class="base-width__320"
              maxlength="200"
              show-word-limit
              type="text"
            ></el-input>
          </span>
        </div>
      </div>
      <div class="base-margin__bottom-20">
        <span>{{ $t('computingCluster.detailsInfo.dataMount') }}</span>
        <span>
          <el-row v-for="(item, index) in mountList" :key="index" class="base-margin__top-10" style="margin-bottom: 0">
            <el-col :span="10" class="base-margin__right-10">
              <el-input
                v-model="item.src"
                :disabled="editDisabled || clusterCount"
                :placeholder="$t('computingCluster.detailsInfo.inputCFSDir')"
                type="text"
              ></el-input>
            </el-col>
            <el-col :span="10">
              <el-input
                v-model="item.dst"
                :disabled="editDisabled || clusterCount"
                :placeholder="$t('computingCluster.detailsInfo.inputMountPath')"
                type="text"
              ></el-input>
            </el-col>
            <el-button
              :disabled="editDisabled || clusterCount"
              class="base-margin__left-10"
              type="text"
              @click="deleteMount(index)"
            >
              {{ $t('computingCluster.detailsInfo.delete') }}
            </el-button>
          </el-row>
        </span>
        <el-button :disabled="editDisabled || clusterCount" type="text" @click="pushMount">
          {{ $t('computingCluster.detailsInfo.addDataMount') }}
        </el-button>
      </div>

      <div class="base-margin__bottom-20">
        <div class="base-flex__cross-center">
          <span>slurm插件</span>
          <span>
            <el-select
              v-model="slurmPlugin"
              :disabled="editDisabled || clusterCount"
              placeholder="无"
              clearable
              multiple
              class="base-margin__right-20 base-width__320"
            >
              <el-option v-for="item in slurmPluginList" :key="item.value" :label="item.label" :value="item.value">
              </el-option>
            </el-select>
          </span>
        </div>
      </div>

      <el-button :disabled="isOwner === 0" type="primary" @click="done">
        {{ editDisabled ? $t('computingCluster.detailsInfo.edit') : $t('computingCluster.detailsInfo.done') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/common/libs/request';
import { Base64 } from 'js-base64';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'baseInfo',
  data() {
    return {
      teamName: '', // 团队名称
      approvalFile: '', // 下载审批状态
      waterMarkRadio: '', // 水印状态
      accountModel: '', // 账户模式
      loginMaster: '', // 节点登录状态
      networkChargeType: '', // 网络计费类型
      deployMode: '', // 部署模式
      homePath: '', // 用户目录路径
      mountList: [], // 数据挂载列表
      loading: false, // 加载状态
      projectId: 0, // 项目ID
      editDisabled: true, // 编辑状态
      clusterCount: false, // 是否有集群
      isOwner: 0, // 是否是项目所有者
      // 网络计费类型选项
      networkChargeTypeList: [],
      slurmPlugin: [],
      slurmPluginList: [
        {
          label: 'pmix',
          value: 'pmix',
        },
        {
          label: 'slurmrestd',
          value: 'slurmrestd',
        },
      ],
      currentProjectInfo: {},
    };
  },
  computed: {
    ...mapState(['app']),
    ...mapGetters('app', ['getCurrentProject']),
  },
  watch: {
    'app.currentProject'(val) {
      const currentProject = this.getCurrentProject();
      this.currentProjectInfo = currentProject;
      if (currentProject.id !== this.projectId) {
        this.isOwner = val.isOwner;
        this.getProjectInfo();
      }
    },
  },

  created() {
    this.networkChargeTypeList = [
      {
        label: this.$t('computingCluster.detailsInfo.bandwidthPrepaid'),
        value: 'BANDWIDTH_PREPAID',
        url: 'https://cloud.tencent.com/document/product/1199/51693#monthly',
      },
      {
        label: this.$t('computingCluster.detailsInfo.trafficPostpaid'),
        value: 'TRAFFIC_POSTPAID_BY_HOUR',
        url: 'https://cloud.tencent.com/document/product/1199/51693#by-traffic',
      },
      {
        label: this.$t('computingCluster.detailsInfo.bandwidthPostpaid'),
        value: 'BANDWIDTH_POSTPAID_BY_HOUR',
        url: 'https://cloud.tencent.com/document/product/1199/51693#by-hour',
      },
      {
        label: this.$t('computingCluster.detailsInfo.bandwidthPackage'),
        value: 'BANDWIDTH_PACKAGE',
        url: 'https://cloud.tencent.com/document/product/1199/51693#bwp',
      },
    ];
  },

  mounted() {
    const currentProject = this.getCurrentProject();
    this.currentProjectInfo = currentProject;
    this.isOwner = currentProject.isOwner;
    this.getProjectInfo();
  },

  methods: {
    /**
     * 完成编辑或保存设置
     * 1. 如果是编辑模式，提交设置到服务器
     * 2. 切换编辑状态
     */
    done() {
      if (!this.editDisabled) {
        const mountList = this.mountList.filter(item => item.src !== '' && item.dst !== '');
        const settings = {
          approvalFile: this.approvalFile.toString(),
          watermark: this.waterMarkRadio.toString(),
          accountModel: this.accountModel,
          homePath: this.homePath,
          mountList: Base64.encode(JSON.stringify({ mount: mountList })),
          loginMaster: this.loginMaster.toString(),
          networkChargeType: this.networkChargeType,
          deployMode: this.deployMode,
        };
        if (this.slurmPlugin.length > 0) {
          settings.slurmPlugin = this.slurmPlugin.join(',');
        }
        postRequest('/api/eda/common/cluster/setClusterSetting', {
          teamName: this.teamName,
          settings,
        }).then(() => {
          this.getProjectInfo();
        });
      }
      this.editDisabled = !this.editDisabled;
    },

    /**
     * 添加数据挂载项
     */
    pushMount() {
      this.mountList.push({ src: '', dst: '' });
    },

    /**
     * 删除数据挂载项
     * @param {number} index - 要删除的挂载项索引
     */
    deleteMount(index) {
      this.mountList.splice(index, 1);
    },

    /**
     * 获取项目信息
     * 1. 从服务器获取项目设置信息
     * 2. 更新组件数据状态
     */
    getProjectInfo() {
      this.loading = true;
      postRequest('/api/eda/common/project/getProjectInfo')
        .then((res) => {
          this.projectId = res.projectId;
          this.teamName = res.teamName;
          this.projectId = res.projectId;
          this.approvalFile = res?.settings?.approvalFile || '0';
          this.waterMarkRadio = res?.settings?.watermark || '0';
          this.loginMaster = res?.settings?.loginMaster || '0';
          this.networkChargeType = res?.settings?.networkChargeType || 'TRAFFIC_POSTPAID_BY_HOUR';
          this.accountModel = res?.settings?.accountModel || 'openldap';
          this.homePath = res?.settings?.homePath || '/home';
          this.deployMode = res?.settings?.deployMode || 'cluster';
          this.mountList = JSON.parse(Base64.decode(res?.settings?.mountList || '{}')).mount || [{ src: '', dst: '' }];
          this.clusterCount = res.clusterCount !== 0;
          this.slurmPlugin = res?.settings?.slurmPlugin ? res.settings.slurmPlugin.split(',') : [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.previewDialog_main--div {
  max-height: 400px;
}

.base-info__container {
  height: 100%;
  overflow: auto;
  background: #fff;
  padding: 10px 20px 30px;

  span:first-child {
    display: inline-block;
    width: 130px;
    margin-right: 20px;
  }
}

.base-info__left,
.base-info__right {
  width: 50%;

  h3 {
    margin-bottom: 30px;
  }
}

.exportPreviewCls {
  padding: 12px 20px;
}
</style>
